<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  BellRing,
  Database,
  Languages,
  RefreshCw,
  Send,
} from 'lucide-vue-next'

import AnalysisSettingsCard from '@/components/AnalysisSettingsCard.vue'
import type {
  AnalysisSettings,
  MemoryRuntimeStatus,
  MonitorStatus,
  NotificationSettings,
  TelegramDialogOption,
} from '@/types/monitor'

const props = defineProps<{
  analysis: AnalysisSettings | null
  status: MonitorStatus
  analysisSaving: boolean
  notification: NotificationSettings | null
  dialogs: TelegramDialogOption[]
  saving: boolean
  memory: MemoryRuntimeStatus
}>()

const emit = defineEmits<{
  saveAnalysis: [settings: AnalysisSettings]
  saveNotification: [settings: NotificationSettings]
  locale: [locale: 'zh-CN' | 'en']
  logout: []
  refreshMemory: []
}>()

const { t, locale } = useI18n()
const memorySearchHealthy = computed(
  () => props.memory.embeddings_ready && !props.memory.search.last_degraded_reason,
)
const draft = ref<NotificationSettings>({
  enabled: false,
  telegram_target_id: null,
  telegram_target_title: null,
})
watch(
  () => props.notification,
  (value) => {
    if (value) draft.value = { ...value }
  },
  { immediate: true, deep: true },
)


function selectTarget(value: number | null): void {
  draft.value.telegram_target_id = value
  draft.value.telegram_target_title =
    props.dialogs.find((dialog) => dialog.telegram_id === value)?.title ?? null
}

function saveNotification(): void {
  emit('saveNotification', { ...draft.value })
}

</script>

<template>
  <section class="tm-view">
    <header class="tm-page-header">
      <div>
        <h1>{{ t('workspace.settings') }}</h1>
        <p>{{ t('workspace.settingsDesc') }}</p>
      </div>
    </header>

    <div class="tm-settings-layout">
      <AnalysisSettingsCard
        :settings="analysis"
        :status="status"
        :saving="analysisSaving"
        @save="emit('saveAnalysis', $event)"
      />

      <article class="tm-card tm-settings-card tm-memory-card">
        <div class="tm-section-heading tm-memory-heading">
          <Database :size="20" />
          <div>
            <h2>{{ t('memorySettings.title') }}</h2>
            <p>{{ t('memorySettings.description') }}</p>
          </div>
          <span :class="['tm-memory-state', { 'tm-memory-state--ok': memorySearchHealthy }]">
            {{ memorySearchHealthy ? t('memorySettings.semanticReady') : t('memorySettings.degraded') }}
          </span>
          <button class="tm-icon-button" :title="t('memorySettings.refresh')" @click="emit('refreshMemory')">
            <RefreshCw :size="15" />
          </button>
        </div>

        <div class="tm-memory-grid">
          <div><span>{{ t('memorySettings.backend') }}</span><strong>{{ memory.search_backend }}</strong></div>
          <div><span>{{ t('memorySettings.indexed') }}</span><strong>{{ memory.counts.ready.toLocaleString() }}</strong></div>
          <div><span>{{ t('memorySettings.pending') }}</span><strong>{{ memory.counts.pending.toLocaleString() }}</strong></div>
          <div><span>{{ t('memorySettings.failed') }}</span><strong>{{ memory.counts.failed.toLocaleString() }}</strong></div>
        </div>

        <div class="tm-memory-detail">
          <span>{{ t('memorySettings.pgvector') }} <strong>{{ memory.pgvector_available ? 'ON' : 'OFF' }}</strong></span>
          <span>{{ t('memorySettings.embedding') }} <strong>{{ memory.embeddings_ready ? `${memory.embedding_dimensions}d` : 'OFF' }}</strong></span>
          <span>{{ t('memorySettings.model') }} <strong>{{ memory.model_id ?? '—' }}</strong></span>
          <span>{{ t('memorySettings.total') }} <strong>{{ memory.counts.total.toLocaleString() }}</strong></span>
          <span>{{ t('memorySettings.backfill') }} <strong>{{ memory.backfill.processed.toLocaleString() }} / {{ memory.backfill.indexed.toLocaleString() }}</strong></span>
          <span>{{ t('memorySettings.searchLatency') }} <strong>{{ memory.search.last_duration_ms }} ms</strong></span>
          <span>{{ t('memorySettings.searchDegraded') }} <strong>{{ memory.search.degraded_requests.toLocaleString() }} / {{ memory.search.requests.toLocaleString() }}</strong></span>
          <span>{{ t('memorySettings.searchTimeouts') }} <strong>{{ memory.search.timeouts.toLocaleString() }}</strong></span>
          <span>{{ t('memorySettings.semanticTimeouts') }} <strong>{{ memory.search.semantic_timeouts.toLocaleString() }}</strong></span>
        </div>
        <p v-if="memory.search.last_degraded_reason" class="tm-memory-error">
          {{ t('memorySettings.searchReason') }}：{{ memory.search.last_degraded_reason }}
        </p>
        <p v-if="memory.last_error || memory.backfill.last_error" class="tm-memory-error">
          {{ memory.last_error ?? memory.backfill.last_error }}
        </p>
      </article>

      <article class="tm-card tm-settings-card">
        <div class="tm-section-heading"><BellRing :size="20" /><div><h2>{{ t('notificationSettings.title') }}</h2><p>{{ t('notificationSettings.description') }}</p></div></div>
        <div class="tm-settings-section tm-settings-section--switch">
          <div><h3>{{ t('notificationSettings.enabled') }}</h3></div>
          <el-switch v-model="draft.enabled" size="large" />
        </div>
        <label class="tm-field tm-field--stacked">
          <span><Send :size="16" />{{ t('notificationSettings.target') }}</span>
          <el-select
            :model-value="draft.telegram_target_id"
            filterable
            clearable
            :disabled="!draft.enabled"
            :placeholder="t('notificationSettings.selectTarget')"
            @update:model-value="selectTarget"
          >
            <el-option
              v-for="dialog in dialogs"
              :key="dialog.telegram_id"
              :label="dialog.username ? `${dialog.title} · @${dialog.username}` : dialog.title"
              :value="dialog.telegram_id"
            />
          </el-select>
          <small v-if="draft.enabled && draft.telegram_target_title">
            {{ t('notificationSettings.current', { target: draft.telegram_target_title }) }}
          </small>
        </label>
        <button class="tm-button tm-button--primary tm-button--save" :disabled="saving" @click="saveNotification">
          <Save :size="17" />{{ t('notificationSettings.save') }}
        </button>
      </article>

      <article class="tm-card tm-settings-card">
        <div class="tm-section-heading"><Languages :size="20" /><div><h2>{{ t('workspace.language') }}</h2></div></div>
        <div class="tm-language-grid">
          <button :class="{ active: locale === 'zh-CN' }" @click="emit('locale', 'zh-CN')">{{ t('workspace.chinese') }}</button>
          <button :class="{ active: locale === 'en' }" @click="emit('locale', 'en')">{{ t('workspace.english') }}</button>
        </div>
      </article>

      <article class="tm-card tm-settings-card tm-danger-zone">
        <div class="tm-section-heading"><LogOut :size="20" /><div><h2>{{ t('workspace.logout') }}</h2></div></div>
        <button class="tm-button tm-button--danger" @click="emit('logout')"><LogOut :size="17" />{{ t('workspace.logout') }}</button>
      </article>
    </div>
  </section>
</template>
